import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { search as searchIcon } from "../../icons/index.js";
import { Button } from "../Button.js";
import { CheckBox } from "../Input/Checkbox.js";
import { SearchBar } from "../Input/SearchBar.js";
import { MultiSelect } from "../Input/MultiSelect.js";
import {
  PageFilterWrapper,
  MobileControls,
  FilterToggleButton,
  SearchIconButton,
  MobileSearchBar,
  SearchIconWrap,
  MobileFilterPanel,
  MobileFilterTopBar,
  FilterPanelInner,
  FilterPanelHeader,
  FilterPanelTitle,
  ResetButton,
  FilterPanelScroll,
  FilterGroupSection,
  FilterGroupLabel,
  FilterPanelFooter,
  DesktopFilterBar,
  DesktopSearchGroup,
  ResultCount,
} from "./styles.js";

export interface FilterOption {
  label: string;
  value: string;
  count?: number;
}

export interface FilterGroup {
  label: string;
  key: string;
  options: FilterOption[];
}

export interface PageFilterProps {
  filterGroups?: FilterGroup[];
  filterToggleText?: string;
  searchLabel?: string;
  searchPlaceholder?: string;
  showAllText?: string;
  /** Uses {shown} and {total}. */
  resultCountText?: string;
  onFilterChange?: (filters: Record<string, string[]>, search: string) => void;
  /** Renders the result count (see `resultCountText`) below the filters. */
  resultCount?: { shown: number; total: number };
}

export const PageFilter: React.FC<PageFilterProps> = ({
  filterGroups = [],
  filterToggleText = "Sorter resultat",
  searchLabel = "Søk",
  searchPlaceholder = "Hva leter du etter?",
  showAllText = "Vis alle",
  resultCountText = "Viser {shown} av {total} resultater",
  onFilterChange,
  resultCount,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showMobileFilter) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showMobileFilter]);

  const toggleFilter = (groupKey: string, value: string) => {
    const current = selectedFilters[groupKey] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const newFilters = { ...selectedFilters, [groupKey]: next };
    setSelectedFilters(newFilters);
    onFilterChange?.(newFilters, searchQuery);
  };

  const clearGroup = (groupKey: string) => {
    const newFilters = { ...selectedFilters, [groupKey]: [] };
    setSelectedFilters(newFilters);
    onFilterChange?.(newFilters, searchQuery);
  };

  const resetAll = () => {
    setSelectedFilters({});
    setSearchQuery("");
    onFilterChange?.({}, "");
  };

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    onFilterChange?.(selectedFilters, q);
  };

  const mobileTopBar = (
    <>
      <MobileControls $withResultCount={!!resultCount}>
        <FilterToggleButton
          type="button"
          aria-expanded={showMobileFilter}
          onClick={() => setShowMobileFilter((v) => !v)}
        >
          <SlidersIcon />
          {filterToggleText}
        </FilterToggleButton>
        <SearchIconButton
          type="button"
          aria-label={searchLabel}
          aria-expanded={showMobileSearch}
          onClick={() => setShowMobileSearch((v) => !v)}
        >
          <SearchIconWrap>{searchIcon}</SearchIconWrap>
        </SearchIconButton>
      </MobileControls>

      {showMobileSearch && (
        <MobileSearchBar
          $withResultCount={!!resultCount}
          value={searchQuery}
          onChange={handleSearch}
          onClear={() => handleSearch("")}
          placeholder={searchPlaceholder}
        />
      )}
    </>
  );

  return (
    <PageFilterWrapper ref={dropdownRef}>
      {/* Mobile top bar */}
      {mobileTopBar}

      {showMobileFilter && (
        <MobileFilterPanel>
          <MobileFilterTopBar>{mobileTopBar}</MobileFilterTopBar>
          <FilterPanelInner>
            <FilterPanelHeader>
              <FilterPanelTitle>Filtrer resultatet</FilterPanelTitle>
              <ResetButton type="button" onClick={resetAll}>
                Nullstill
              </ResetButton>
            </FilterPanelHeader>
            <FilterPanelScroll>
              {filterGroups.map((group) => (
                <FilterGroupSection key={group.key}>
                  <FilterGroupLabel>{group.label}</FilterGroupLabel>
                  {group.options.map((option) => {
                    const checked = (
                      selectedFilters[group.key] || []
                    ).includes(option.value);
                    return (
                      <CheckBox
                        key={option.value}
                        id={`${group.key}-${option.value}`}
                        checked={checked}
                        onChange={() => toggleFilter(group.key, option.value)}
                        label={`${option.label}${
                          option.count !== undefined ? ` (${option.count})` : ""
                        }`}
                      />
                    );
                  })}
                </FilterGroupSection>
              ))}
            </FilterPanelScroll>
            <FilterPanelFooter>
              <Button
                $variant="primary"
                onClick={() => setShowMobileFilter(false)}
              >
                Lagre filter
              </Button>
            </FilterPanelFooter>
          </FilterPanelInner>
        </MobileFilterPanel>
      )}

      {/* Desktop filter bar */}
      <DesktopFilterBar $withResultCount={!!resultCount}>
        {filterGroups.map((group) => {
          const selected = selectedFilters[group.key] || [];
          const isOpen = openDropdown === group.key;
          return (
            <MultiSelect
              key={group.key}
              label={group.label}
              options={group.options}
              selected={selected}
              isOpen={isOpen}
              onToggle={() => setOpenDropdown(isOpen ? null : group.key)}
              onSelect={(value) => toggleFilter(group.key, value)}
              onClear={() => clearGroup(group.key)}
              placeholder={showAllText}
            />
          );
        })}

        <DesktopSearchGroup>
          <SearchBar
            label={searchLabel}
            value={searchQuery}
            onChange={handleSearch}
            onClear={() => handleSearch("")}
            placeholder={searchPlaceholder}
          />
        </DesktopSearchGroup>
      </DesktopFilterBar>

      {resultCount && (
        <ResultCount role="status">
          {renderResultCount(resultCountText, resultCount)}
        </ResultCount>
      )}
    </PageFilterWrapper>
  );
};

/** Fills {shown} (bold) and {total} into the result count template. */
function renderResultCount(
  template: string,
  { shown, total }: { shown: number; total: number }
) {
  return template.split(/(\{shown\}|\{total\})/).map((part, i) => {
    if (part === "{shown}") return <strong key={i}>{shown}</strong>;
    if (part === "{total}") return <React.Fragment key={i}>{total}</React.Fragment>;
    return part;
  });
}

function SlidersIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4 6H20M4 12H20M4 18H20"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle
        cx="8"
        cy="6"
        r="2.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle
        cx="16"
        cy="12"
        r="2.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle
        cx="10"
        cy="18"
        r="2.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

